import {ControlledAutocompleteSelect, Pill} from '@hm/fundamental'
import React, {FC, useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE, SERIES_COLORS} from '../../consts'
import {SERIES_BG_COLORS, SERIES_BORDER_COLORS} from '../../consts/chart.consts'
import {ChartViewMode, DifficultyFilter, ValueDisplayMode} from '../../types'

import {CategoryFilterDropdown} from './CategoryFilterDropdown'
import {ChartViewToggle} from './ChartViewToggle'
import {DifficultyFilterDropdown} from './DifficultyFilterDropdown'
import {ValueDisplayToggle} from './ValueDisplayToggle'

type HierarchyEntry = {id: string; name: string}

type ChartToolbarProps = {
  hierarchyEntries: HierarchyEntry[]
  visibleSeriesIds: string[] | null
  onVisibleSeriesIdsChange: (ids: string[] | null) => void
  categoryFilter: string[]
  onCategoryFilterChange: (sectionIds: string[]) => void
  difficultyFilter: DifficultyFilter
  onDifficultyFilterChange: (filter: DifficultyFilter) => void
  viewMode: ChartViewMode
  onViewModeChange: (mode: ChartViewMode) => void
  valueDisplayMode: ValueDisplayMode
  onValueDisplayModeChange: (mode: ValueDisplayMode) => void
  selectedYear: number
  onSelectedYearChange: (year: number) => void
  availableYears: number[]
}

export const ChartToolbar: FC<ChartToolbarProps> = ({
  hierarchyEntries,
  visibleSeriesIds,
  onVisibleSeriesIdsChange,
  categoryFilter,
  onCategoryFilterChange,
  difficultyFilter,
  onDifficultyFilterChange,
  viewMode,
  onViewModeChange,
  valueDisplayMode,
  onValueDisplayModeChange,
  selectedYear,
  onSelectedYearChange,
  availableYears
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const yearOptions = useMemo(
    () => [...availableYears].reverse().map((y) => ({label: String(y), value: String(y)})),
    [availableYears]
  )

  const effectiveVisibleIds = useMemo(
    () => (!visibleSeriesIds ? hierarchyEntries.map((e) => e.id) : visibleSeriesIds),
    [visibleSeriesIds, hierarchyEntries]
  )

  const toggleSeries = useCallback(
    (nodeId: string) => {
      if (!visibleSeriesIds) {
        onVisibleSeriesIdsChange([nodeId])
        return
      }
      const isCurrentlyVisible = visibleSeriesIds.includes(nodeId)
      if (isCurrentlyVisible) {
        const newVisible = visibleSeriesIds.filter((id) => id !== nodeId)
        if (newVisible.length === 0) {
          onVisibleSeriesIdsChange(null)
          return
        }
        onVisibleSeriesIdsChange(newVisible)
      } else {
        const newVisible = [...visibleSeriesIds, nodeId]
        if (newVisible.length === hierarchyEntries.length) {
          onVisibleSeriesIdsChange(null)
          return
        }
        onVisibleSeriesIdsChange(newVisible)
      }
    },
    [visibleSeriesIds, hierarchyEntries, onVisibleSeriesIdsChange]
  )

  return (
    <div className="flex flex-wrap items-center justify-between gap-y-2 mb-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Pill
          text={t('comparativeAnalysis.chart.seriesFilter.all')}
          size="regular"
          variant="shade"
          type={!visibleSeriesIds ? 'primary' : 'neutral'}
          className="cursor-pointer"
          onClick={() => onVisibleSeriesIdsChange(null)}
          data-cy="series-pill-all"
        />
        {hierarchyEntries.map((entry, index) => {
          const colorIdx = index % SERIES_COLORS.length
          const color = SERIES_COLORS[colorIdx]
          const bgColor = SERIES_BG_COLORS[colorIdx]
          const borderColor = SERIES_BORDER_COLORS[colorIdx]
          const isActive = !visibleSeriesIds || effectiveVisibleIds.includes(entry.id)
          return (
            <div
              key={entry.id}
              style={{
                backgroundColor: isActive ? bgColor : undefined,
                borderColor: isActive ? borderColor : undefined,
                borderWidth: 1,
                borderStyle: 'solid',
                color: color,
                opacity: isActive ? 1 : 0.5,
                borderRadius: '999px'
              }}
            >
              <Pill
                text={entry.name}
                size="regular"
                variant="shade"
                type="neutral"
                className="cursor-pointer !bg-transparent !border-none !text-[inherit]"
                onClick={() => toggleSeries(entry.id)}
                data-cy={`series-pill-${entry.id}`}
              />
            </div>
          )
        })}
      </div>
      <div className="flex items-center gap-3 flex-wrap justify-end">
        <CategoryFilterDropdown value={categoryFilter} onChange={onCategoryFilterChange} />
        <DifficultyFilterDropdown value={difficultyFilter} onChange={onDifficultyFilterChange} />
        {viewMode === 'monthly' && (
          <div className="w-full max-w-[140px] [&_[data-cy='year-selector-clear']]:hidden">
            <ControlledAutocompleteSelect
              label={t('comparativeAnalysis.chart.yearSelector.label')}
              options={yearOptions}
              selectedValues={[String(selectedYear)]}
              onChange={(values) => {
                const selected = values[values.length - 1]
                if (selected) onSelectedYearChange(Number(selected))
              }}
              data-cy="year-selector"
            />
          </div>
        )}
        <ChartViewToggle value={viewMode} onChange={onViewModeChange} />
        <ValueDisplayToggle value={valueDisplayMode} onChange={onValueDisplayModeChange} />
      </div>
    </div>
  )
}
